import { Box, Typography } from '@mui/material';

import { NextBadge } from './NextBadge';

interface AppointmentItemProps {
  time: string;
  patientName: string;
  duration: string;
  appointmentType: string;
  isNext?: boolean;
  isFirst?: boolean;
  onClick: () => void;
}

/**
 * Pure UI component for displaying a single appointment
 * Shows time, patient info, and highlights the next appointment
 */
export const AppointmentItem: React.FC<AppointmentItemProps> = ({
  time,
  patientName,
  duration,
  appointmentType,
  isNext = false,
  isFirst = false,
  onClick,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 2,
        backgroundColor: isNext ? 'primary.main' : 'transparent',
        color: isNext ? 'white' : 'text.primary',
        cursor: 'pointer',
        transition: 'background-color 0.2s, margin 0.2s',
        borderTop: isFirst ? 'none' : '1px solid',
        borderColor: 'borderColors.primary',
        position: 'relative',
        '&:hover': {
          backgroundColor: isNext ? 'primary.dark' : 'rgba(255, 255, 255, 0.5)',
        },
      }}
    >
      {/* Time on the left */}
      <Typography
        variant="body2"
        sx={{
          minWidth: 65,
          fontWeight: 600,
          color: isNext ? 'white' : 'text.secondary',
          fontSize: '0.875rem',
        }}
      >
        {time}
      </Typography>

      {/* Patient info in the middle */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'inherit',
            fontSize: '0.9375rem',
          }}
        >
          {patientName} ({duration}) {appointmentType}
        </Typography>
      </Box>

      {/* Next badge on the right for the first appointment */}
      {isNext && <NextBadge />}
    </Box>
  );
};
